import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Layout from '@/components/Layout';
import PlayAnimation from '@/components/PlayAnimation';
import { api } from '@/lib/api';
import { ChevronLeft, ShieldCheck, ShoppingCart, Loader, Check } from 'lucide-react';
import { toast } from 'sonner';
import { useLang } from '@/context/LangContext';

export default function MarketplaceListing() {
  const { t } = useLang();
  const { playId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(false);
  const [bought, setBought] = useState(false);

  useEffect(() => {
    api.get(`/marketplace/listings/${playId}`)
      .then(r => setData(r.data))
      .finally(() => setLoading(false));
  }, [playId]);

  const buy = async () => {
    setBuying(true);
    try {
      const r = await api.post(`/marketplace/purchase/${playId}`);
      toast.success(t('marketplace_purchase_success') || '¡Compra exitosa! Jugada añadida a tu playbook');
      setBought(true);
      setTimeout(() => navigate(`/playbook/${r.data.copy_play_id}`), 1200);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(detail || t('marketplace_purchase_error') || 'Error en la compra');
    } finally {
      setBuying(false);
    }
  };

  if (loading) return <Layout><div className="text-gray-400">{t('loading') || 'Cargando...'}</div></Layout>;
  if (!data) return <Layout><div className="text-gray-400">{t('marketplace_listing_not_found') || 'Listing no encontrado.'}</div></Layout>;

  const { play, coach } = data;

  return (
    <Layout>
      <Link to="/marketplace" className="inline-flex items-center gap-1 text-gold hover:underline mb-4 text-sm uppercase tracking-wider font-h">
        <ChevronLeft className="w-4 h-4" /> {t('marketplace_back') || 'Volver al Marketplace'}
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <div className="flex items-start justify-between mb-4 gap-4 flex-wrap">
            <div>
              <div className="text-xs font-mono uppercase tracking-[0.3em] text-gold mb-1">{play.category} · {play.subcategory} · {play.difficulty}</div>
              <h1 className="font-h uppercase text-4xl leading-tight">{play.name}</h1>
            </div>
            <div className="font-mono text-gold border-2 border-gold/60 rounded-md px-3 py-2 text-lg">
              {Math.round((play.success_rate || 0.7) * 100)}%
            </div>
          </div>
          <PlayAnimation play={play} autoplay />
        </div>

        <div className="space-y-4">
          {coach && (
            <div className="bg-card-steel border-2 border-gold rounded-lg p-5">
              <div className="text-xs font-mono uppercase tracking-widest text-gold mb-3">{t('marketplace_elite_coach') || 'Coach Élite'}</div>
              <Link to={`/marketplace/coach/${coach.coach_id}`} className="flex items-center gap-3 mb-3">
                {coach.photo_url ? (
                  <img src={coach.photo_url} className="w-14 h-14 rounded-full border-2 border-gold object-cover" />
                ) : (
                  <div className="w-14 h-14 rounded-full border-2 border-gold bg-gold/10" />
                )}
                <div>
                  <div className="font-h uppercase text-lg text-white flex items-center gap-1">
                    {coach.display_name}
                    <ShieldCheck className="w-4 h-4 text-gold" />
                  </div>
                  <div className="text-xs text-gold uppercase tracking-widest">{coach.credentials}</div>
                </div>
              </Link>
              <p className="text-sm text-gray-300">{coach.bio}</p>
            </div>
          )}

          <div className="bg-card-steel border border-gold/30 rounded-lg p-5">
            <div className="text-xs font-mono uppercase tracking-widest text-gold mb-2">{t('marketplace_about_play') || 'Sobre esta jugada'}</div>
            <p className="text-gray-200 text-sm">{play.listing_blurb || play.description}</p>
          </div>

          <div className="bg-gold text-navy rounded-lg p-5 border-2 border-gold">
            <div className="font-h text-5xl mb-3">${play.price?.toFixed(2)}</div>
            <button
              data-testid="purchase-btn"
              onClick={buy}
              disabled={buying || bought}
              className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md bg-navy text-gold font-h uppercase tracking-wider font-bold hover:bg-navy-deep disabled:opacity-60 transition">
              {bought ? <Check className="w-4 h-4" /> : buying ? <Loader className="w-4 h-4 animate-spin" /> : <ShoppingCart className="w-4 h-4" />}
              {bought ? (t('marketplace_purchased') || 'Comprada') : buying ? (t('processing') || 'Procesando...') : (t('marketplace_buy_cta') || 'Comprar y añadir a mi playbook')}
            </button>
            <div className="text-xs text-navy/70 text-center mt-2">MOCKED · Stripe integration coming</div>
          </div>

          <div className="bg-card-steel border border-gold/20 rounded-lg p-4 text-xs text-gray-400">
            <span className="text-gold uppercase tracking-widest">{play.purchases_count || 0}</span> {t('marketplace_coaches_own') || 'coaches ya tienen esta jugada en su playbook.'}
          </div>
        </div>
      </div>
    </Layout>
  );
}
